import React, { useMemo } from 'react';
import { calculateTemplateDueDate } from '#/modules/workOrder/utils/calculateTemplateDueDate';
import { useSearchParams } from 'react-router-dom';
import WorkOrderForm from '../WorkOrderForm/WorkOrderForm';
import useWorkOrderCreate from '../../hooks/useWorkOrderCreate';
import type { WorkOrderDetails, WorkOrderInput } from '#/graphql/types';
import If from '#/common/components/If';
import useWOTemplateQuery from '#/modules/wo-template/hooks/useWOTemplateQuery';
import newFields from './new-fields';
import { categories, impacts, priorities, states } from '../../mappings/mappings';
import { BLANK_TEXT } from '#/modules/wo-template/utils/constants';
import useFindingQuery from '#/modules/findings/hooks/useFindingQuery';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';
import useAlertQuery from '#/modules/alert/hooks/useAlertQuery';
import { generateDescription, getHighestImpactPriority } from '#/modules/alert/utils/constants';

export interface NewWorkOrderFormProps {
  templateId: string;
  findingId: string;
  title: string;
  alertId: string;
}

const NewWorkOrderForm: React.FC<NewWorkOrderFormProps> = ({
  title,
  templateId,
  findingId,
  alertId,
}) => {
  const [searchParams] = useSearchParams();
  const assetIdFromUrl = searchParams.get('assetId') || undefined;

  const templateQuery = useWOTemplateQuery(templateId);
  const template = templateQuery.data?.getWorkOrderTemplate?.data;

  // Query finding data when findingId is provided
  const findingQuery = useFindingQuery(findingId);
  const finding = findingQuery.data?.getFinding?.data;
  const alertQuery = useAlertQuery(alertId ?? '', {
    skip: !alertId,
  });
  const alert = alertQuery.data?.getAlert?.data;

  const newWo = useMemo(() => {
    if (!templateId) {
      return undefined;
    }

    let baseWorkOrder: Partial<WorkOrderDetails & WorkOrderInput>;

    if (templateId === BLANK_TEXT || !template) {
      baseWorkOrder = {
        state: states.open.key,
        category: categories.corrective.key,
        createdAt: new Date().toISOString(),
        actualCostValue: 0,
        assetId: assetIdFromUrl,
      };
    } else {
      baseWorkOrder = {
        type: template.type,
        category: template.category,
        title: template.title,
        state: states.open.key,
        priority: template.priority
          ? priorities[Number(template.priority) as keyof typeof priorities]?.value
          : null,
        impact: template.impact
          ? impacts[Number(template.impact) as keyof typeof impacts]?.value
          : null,
        assignmentGroupId: template.assignmentGroupId,
        notes: template.notes,
        description: template.description,
        templateId: template.id,
        userId: template.userId,
        locationId: template.locationId,
        estimatedCostValue: template.estimatedCostValue,
        estimatedEffort: template.estimatedEffort,
        dueDate: calculateTemplateDueDate(template.executionInterval),
        createdAt: new Date().toISOString(),
        actualCostValue: 0,
        assetId: assetIdFromUrl,
        assignedToId: template.assignedToId,
      };
    }

    // Override with finding data if findingId is provided and finding is loaded
    if (findingId && finding) {
      return {
        ...baseWorkOrder,
        locationId: finding.locationId,
        title: finding.shortDescription,
        description: finding.description,
        tenantId: finding.tenantId,
        findingId: finding.id,
      } as WorkOrderDetails;
    }

    // Override with alert data if alertId is provided and alert is loaded
    if (alertId && alert) {
      return {
        ...baseWorkOrder,
        alertId: alert.id,
        title: alert.exceptionName,
        category: categories.corrective.key,
        state: states.open.key,
        tenantId: alert.tenantId,
        description: generateDescription(alert),
        priority: getHighestImpactPriority(
          alert.impactComfort,
          alert.impactEnergy,
          alert.impactReliability,
          'workOrder',
        ),
      } as WorkOrderDetails;
    }

    return baseWorkOrder;
  }, [template, templateId, findingId, finding, alertId, alert, assetIdFromUrl]) as
    | WorkOrderDetails
    | undefined;

  const { createWorkOrder } = useWorkOrderCreate();

  // Show loading if template is loading or if we're waiting for finding data
  const isLoading =
    templateQuery.loading ||
    (!!findingId && findingQuery.loading) ||
    (!!alertId && alertQuery.loading);

  return (
    <>
      <If condition={isLoading}>
        <StandardThrobber />
      </If>
      <If condition={!!newWo && !isLoading}>
        <WorkOrderForm
          title={title}
          workOrder={newWo!}
          fields={newFields}
          submit={createWorkOrder}
        />
      </If>
    </>
  );
};

export default React.memo(NewWorkOrderForm);
